import React from 'react';
import { useTranslation } from 'react-i18next';
import { AlertCircle, Loader2, Search } from 'lucide-react';
import type { OrgMember } from '@examing/types';
import Avatar from 'components/ui/avatar';
import EmptyState from 'components/ui/EmptyState';
import Skeleton from 'components/ui/skeleton';
import { StatusBadge } from 'components/ui/status-badge';
import { LoadMore } from 'components/ui/pagination';
import { cn } from 'lib/utils';
import { STATUS_TONE, lastActiveLabel, memberStatus, roleLabel, statusLabel } from './memberLabels';

/** One bordered card of hairline-divided rows (the phone list card, in theme tokens). */
const LIST_CARD = 'divide-y divide-border overflow-hidden rounded-lg border border-border bg-card';

function PhoneListSkeleton() {
  return (
    <div className={LIST_CARD} aria-hidden>
      {[0, 1, 2, 3, 4].map((row) => (
        <div key={row} className="flex items-center gap-3 px-3.5 py-3">
          <Skeleton className="h-8 w-8 rounded-full" />
          <div className="flex flex-1 flex-col gap-1.5">
            <Skeleton className={cn('h-4', row % 2 ? 'w-28' : 'w-40')} />
            <Skeleton className="h-3 w-32" />
          </div>
          <Skeleton className="h-5 w-14" />
        </div>
      ))}
    </div>
  );
}

interface MembersPhoneListProps {
  members: OrgMember[];
  total: number;
  /** Rows loaded per "load more". */
  step: number;
  isLoading: boolean;
  isError: boolean;
  /** The previous results stay, dimmed, while a new search loads. */
  isStale: boolean;
  hasFilters: boolean;
  onRetry: () => void;
  onClearFilters: () => void;
  onLoadMore: () => void;
  loadingMore: boolean;
  /** The member whose action is in flight; their row shows a spinner. */
  busyId: string | null;
  onOpenMember: (member: OrgMember) => void;
}

/** Phones: one card of member rows (name, "role · last active", status) and a one-line "load more". */
export function MembersPhoneList({
  members,
  total,
  step,
  isLoading,
  isError,
  isStale,
  hasFilters,
  onRetry,
  onClearFilters,
  onLoadMore,
  loadingMore,
  busyId,
  onOpenMember,
}: MembersPhoneListProps) {
  const { t, i18n } = useTranslation();

  if (isLoading) return <PhoneListSkeleton />;
  if (isError) {
    return (
      <EmptyState
        icon={<AlertCircle />}
        title={t('failed_to_load_members')}
        secondaryAction={{ label: t('retry'), onClick: onRetry }}
      />
    );
  }
  if (members.length === 0) {
    return (
      <EmptyState
        icon={<Search />}
        title={t('no_members_found')}
        description={t('try_adjusting_your_search_or_filters')}
        secondaryAction={hasFilters ? { label: t('clear_filters'), onClick: onClearFilters } : undefined}
      />
    );
  }

  return (
    <div className={cn('flex flex-col gap-3.5 transition-opacity', isStale && 'opacity-60')} aria-busy={isStale || undefined}>
      <div className={LIST_CARD}>
        {members.map((member) => {
          const status = memberStatus(member);
          const name = member.user?.name;
          const contact = member.user?.email || member.user?.phone;
          const lastActive = lastActiveLabel(member, t, i18n.language);
          return (
            <button
              key={member.id}
              type="button"
              onClick={() => onOpenMember(member)}
              disabled={busyId === member.id}
              className="flex w-full items-center gap-3 px-3.5 py-3 text-left transition-colors hover:bg-muted/40 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-ring disabled:opacity-60"
            >
              <Avatar name={name} src={member.user?.avatarUrl} size="sm" />
              <span className="flex min-w-0 flex-1 flex-col gap-0.5">
                <span className="truncate text-[15px] font-medium leading-[18px] text-foreground">{name || contact || '—'}</span>
                <span className="truncate text-[12.5px] leading-4 text-muted-foreground">
                  {roleLabel(member.role, t)} · {lastActive}
                </span>
              </span>
              {busyId === member.id ? (
                <Loader2 className="h-4 w-4 shrink-0 animate-spin text-muted-foreground" aria-hidden />
              ) : (
                <StatusBadge tone={STATUS_TONE[status] ?? 'neutral'} className="shrink-0">
                  {statusLabel(status, t)}
                </StatusBadge>
              )}
            </button>
          );
        })}
      </div>
      <LoadMore compact shown={members.length} total={total} step={step} onLoadMore={onLoadMore} loading={loadingMore} />
    </div>
  );
}

export default MembersPhoneList;
